import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { FaLock, FaExclamationTriangle } from 'react-icons/fa';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

/**
 * Componente que protege rutas de cursos
 * Verifica que el usuario tenga acceso al curso antes de permitir visualizarlo
 */
const ProtectedCourseRoute = ({ user, children }) => {
  const { courseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user?.email) {
        console.log('❌ No hay usuario logueado');
        setLoading(false);
        setHasAccess(false);
        return;
      }

      if (!courseId) {
        console.log('❌ No hay courseId en la URL');
        setLoading(false);
        setHasAccess(false);
        return;
      }

      console.log('🔐 Verificando acceso al curso:', courseId);
      console.log('👤 Usuario:', user.email);

      try {
        const { data, error } = await supabase
          .rpc('perfil_portal', { p_correo: user.email });

        if (error) {
          console.error('❌ Error consultando Supabase:', error);
          setError('Error al verificar acceso');
          setLoading(false);
          setHasAccess(false);
          return;
        }

        if (!data?.Courses) {
          console.log('⚠️ Usuario no tiene cursos asignados');
          setLoading(false);
          setHasAccess(false);
          return;
        }

        // Normalizar cursos a array en minúsculas
        const coursesArray = typeof data.Courses === 'string'
          ? data.Courses.split(',').map(c => c.trim().toLowerCase())
          : Array.isArray(data.Courses)
          ? data.Courses.map(c => c.toLowerCase())
          : [];

        const courseIdNormalized = courseId.toLowerCase();
        const userHasAccess = coursesArray.includes(courseIdNormalized);

        console.log('📋 Cursos del usuario:', coursesArray);
        console.log('🔍 Buscando acceso a:', courseIdNormalized);
        console.log(userHasAccess ? '✅ Acceso PERMITIDO' : '❌ Acceso DENEGADO');

        setHasAccess(userHasAccess);
        setLoading(false);

      } catch (err) {
        console.error('❌ Error en checkAccess:', err);
        setError('Error al verificar acceso');
        setLoading(false);
        setHasAccess(false);
      }
    };

    checkAccess();
  }, [user, courseId]);

  // Loading state
  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f4f47'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid rgba(255,255,255,0.2)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 1.5rem'
          }} />
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
            Verificando acceso...
          </h3>
          <p style={{ margin: 0, opacity: 0.9 }}>
            Un momento por favor
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Sin acceso - Mostrar página de bloqueo
  if (!hasAccess) {
    return (
      <AccessDenied 
        courseId={courseId}
        user={user}
        error={error}
      />
    );
  }

  // Con acceso - Renderizar children (CoursePlatform)
  return children;
};

// Componente de acceso denegado
const AccessDenied = ({ courseId, user, error }) => {
  const courseTitles = {
    'seace': 'SEACE - Sistema Electrónico de Contrataciones',
    'pac': 'Plan Anual de Contrataciones (PAC)',
    'contratos-menores': 'Contratos Menores'
  };

  const courseColors = {
    'seace': '#DC2626',
    'pac': '#7C3AED',
    'contratos-menores': '#0066CC'
  };

  const courseTitle = courseTitles[courseId] || 'Curso';
  const courseColor = courseColors[courseId] || '#3b82f6';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: courseColor,
          padding: '3rem 2rem',
          textAlign: 'center',
          position: 'relative'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            backdropFilter: 'blur(10px)'
          }}>
            <FaLock size={50} color="white" />
          </div>
          <h1 style={{
            color: 'white',
            margin: '0 0 0.5rem 0',
            fontSize: '2rem',
            fontWeight: '700'
          }}>
            Acceso Restringido
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.9)',
            margin: 0,
            fontSize: '1.1rem'
          }}>
            {courseTitle}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '2.5rem 2rem' }}>
          {error ? (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #fca5a5',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem'
            }}>
              <FaExclamationTriangle size={24} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#991b1b', fontSize: '1.1rem' }}>
                  Error de Verificación
                </h3>
                <p style={{ margin: 0, color: '#7f1d1d', lineHeight: '1.6' }}>
                  {error}. Por favor, intenta nuevamente o contacta a soporte.
                </p>
              </div>
            </div>
          ) : (
            <div style={{
              background: '#fef3c7',
              border: '2px solid #fbbf24',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem'
            }}>
              <FaExclamationTriangle size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#78350f', fontSize: '1.1rem' }}>
                  No tienes acceso a este curso
                </h3>
                <p style={{ margin: 0, color: '#92400e', lineHeight: '1.6' }}>
                  Este contenido está disponible únicamente para usuarios que han adquirido el curso.
                </p>
              </div>
            </div>
          )}

          {user ? (
            <div style={{
              background: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '2rem'
            }}>
              <p style={{
                margin: '0 0 0.5rem 0',
                fontSize: '0.9rem',
                color: '#6b7280',
                fontWeight: '600'
              }}>
                Sesión actual:
              </p>
              <p style={{
                margin: 0,
                fontSize: '1rem',
                color: '#1f2937',
                fontWeight: '500'
              }}>
                {user.email}
              </p>
            </div>
          ) : (
            <div style={{
              background: '#fef2f2',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '2rem',
              border: '2px solid #fca5a5'
            }}>
              <p style={{
                margin: 0,
                color: '#991b1b',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                No has iniciado sesión
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <a
              href="/cursos/academia"
              style={{
                display: 'block',
                padding: '1rem 1.5rem',
                background: courseColor,
                color: 'white',
                textAlign: 'center',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                boxShadow: `0 4px 12px ${courseColor}66`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 8px 20px ${courseColor}88`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${courseColor}66`;
              }}
            >
              Ver Cursos Disponibles
            </a>

            <a
              href={`https://wa.me/942128139?text=${encodeURIComponent(`Hola! Estoy interesado en adquirir acceso al curso: ${courseTitle}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                padding: '1rem 1.5rem',
                background: '#25D366',
                color: 'white',
                textAlign: 'center',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(37, 211, 102, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.4)';
              }}
            >
              💬 Consultar por WhatsApp
            </a>

            <a
              href="/"
              style={{
                display: 'block',
                padding: '1rem 1.5rem',
                background: 'white',
                color: '#6b7280',
                textAlign: 'center',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1rem',
                border: '2px solid #e5e7eb',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.color = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              Volver al Inicio
            </a>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem 2rem',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            fontSize: '0.9rem',
            color: '#6b7280',
            lineHeight: '1.6'
          }}>
            Si crees que esto es un error o ya adquiriste el curso,{' '}
            <a
              href={`https://wa.me/942128139?text=${encodeURIComponent('Hola! Necesito ayuda con el acceso a mi curso')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: courseColor,
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              contáctanos aquí
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProtectedCourseRoute;